import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/ai-incident-management", label: "AI incident management" },
  { to: "/aiops-incident-reasoning", label: "AIOps incident reasoning" },
  { to: "/major-incident-management", label: "Major incident management" },
  { to: "/it-downtime-reduction", label: "IT downtime reduction" },
];

const FAQ = [
  {
    q: "What is root cause analysis AI?",
    a: "Root cause analysis AI uses machine reasoning to help teams identify the most likely cause of an incident. CREAIOps generates ranked RCA hypotheses with supporting reasoning from multiple domain agents — so engineers can validate the right candidate faster.",
  },
  {
    q: "Does CREAIOps guarantee a single correct root cause?",
    a: "No. CREAIOps surfaces likely hypotheses ranked by weighted consensus and supporting context. A qualified human reviewer is required to validate the cause before acting.",
  },
  {
    q: "Can CREAIOps replace post-incident reviews?",
    a: "CREAIOps accelerates the analysis phase and produces an auditable reasoning trail, but does not replace the human-led post-incident review. It gives reviewers a structured starting point.",
  },
  {
    q: "How does multi-agent reasoning improve RCA quality?",
    a: "Specialist agents look at application, infrastructure, network, security, database, and business-impact lenses independently. Combining their hypotheses reduces single-perspective bias and surfaces causes that one team might overlook.",
  },
  {
    q: "Does CREAIOps store sensitive production logs?",
    a: "Use anonymized inputs only. CREAIOps is designed to reason from anonymized incident context, not raw production data, secrets, or regulated information.",
  },
];

export default function RootCauseAnalysisAI() {
  return (
    <SeoLandingLayout
      slug="root-cause-analysis-ai"
      metaTitle="Root Cause Analysis AI for IT Incidents | CREAIOps"
      metaDescription="Accelerate root cause analysis with CREAIOps. Multi-agent AI reasoning produces ranked RCA hypotheses with supporting evidence across application, infrastructure, network, security, database, and business impact."
      h1="Root cause analysis AI for complex IT incidents"
      intro="CREAIOps brings AI-assisted root cause analysis to the moments that matter most. Instead of waiting until the post-incident review, on-call teams get ranked RCA hypotheses, supporting reasoning, and a clear path to validation — all reviewable by a human before any change."
      problem={{
        title:
          "RCA usually arrives too late to change the outcome of an incident",
        body:
          "Traditional RCA happens after the incident is closed, when memory and signals have already faded. During the incident, the team is reacting; days later, someone writes up the conclusion. The result is slower recovery, repeat incidents, and weaker problem management.",
        points: [
          "Live incidents prioritise restoration; reasoning is deferred",
          "Different teams hold partial views of the same incident",
          "Post-incident write-ups are inconsistent across shifts and reviewers",
          "Repeat incidents recur because the underlying cause is never fully validated",
        ],
      }}
      howWeHelp={{
        title:
          "Bring structured RCA reasoning into the live incident, not after it",
        body:
          "CREAIOps generates RCA hypotheses while the incident is still open. Multi-agent reasoning spans every relevant lens — application, infrastructure, network, security, database, and business impact — and ranks the most likely causes for human validation.",
        items: [
          {
            title: "Ranked RCA hypotheses",
            desc: "Likely causes scored by weighted consensus, each with the reasoning that produced it.",
          },
          {
            title: "Evidence-backed reasoning",
            desc: "Agents publish the context, assumptions, and observations they relied on — making validation faster.",
          },
          {
            title: "Specialist lens coverage",
            desc: "Application, infrastructure, network, security, database, and business-impact perspectives in a single workspace.",
          },
          {
            title: "Audit-ready RCA artefacts",
            desc: "Exportable reasoning trail and approved runbook draft for problem management and compliance.",
          },
        ],
      }}
      benefits={[
        "Faster identification of the most plausible root cause",
        "Stronger inputs into problem management and prevention",
        "Reduced bias from any single team's perspective",
        "Consistent RCA quality across shifts and regions",
        "Clearer narrative for executive and customer communications",
      ]}
      useCase={{
        title:
          "A SaaS provider uses CREAIOps to investigate recurring API timeouts",
        body:
          "After a series of repeat timeouts, the SRE lead submits the anonymized scenario into CREAIOps. Multi-agent reasoning highlights a database-pool exhaustion hypothesis alongside an upstream rate-limit hypothesis — both with supporting context. The team validates the database hypothesis and approves a runbook draft for the next occurrence.",
        steps: [
          "Submit anonymized incident scenario to CREAIOps",
          "Multi-agent reasoning produces ranked RCA hypotheses",
          "Evidence and context are reviewed by SRE and database leads",
          "Approval-ready runbook is generated for the validated cause",
          "Reasoning trail is exported into problem management",
        ],
      }}
      faq={FAQ}
      related={RELATED}
    />
  );
}
